import React, { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from "react";
import styled from 'styled-components';
import { Button, Modal, PageHeader, Popconfirm, Table, Tag, Image, Input, Tabs, Tooltip, type TabsProps } from "antd";
import { path } from 'ramda';

import useQuery from "../hooks/useQuery";
import usePaginatedRecordList from "../hooks/usePaginatedRecordList";
import { Identifiable, IContentType, RecordFieldSchema, RecordModel, RecordsetFilters } from "../types/core";
import RecordDrawer from "./RecordDrawer";
import RecordGalleriesModal from "./RecordGalleriesModal";
import FloorPlanWizard from "./business/floorPlans/FloorPlanWizard";
import ChangeLogDrawer from "./ChangeLogDrawer";
import { CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, SettingOutlined, LoginOutlined, EditOutlined, FolderAddOutlined, HistoryOutlined, PictureOutlined } from "@ant-design/icons";
import GenericRelationLabel from "./GenericRelationLabel";
import { ButtonType } from "antd/lib/button/button";
import RecordSelect from "./fields/RecordSelect";
import { SizeType } from "antd/lib/config-provider/SizeContext";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import RecordFieldChoices from "./RecordFieldChoices";
import RelatedFieldCell from "./fields/RelatedFieldCell";
import RecordCustomAction from "./RecordCustomAction";
import ExportCsvModal from "./ExportCsvModal";
import ManyToManyRecordTable from "./ManyToManyRecordTable";
import { financeAmtFormat } from "../utils";
import CustomActionButton from "./CustomActionButton";
import { useTheme } from "../contexts/ThemeProvider";
import { ColumnsType } from "antd/es/table";

const { Search } = Input;

const Container = styled.div<any>`
  height: 100%;

  .ant-table-pagination {
    position: ${({ fullpage }) => fullpage ? 'relative' : 'sticky'};
    bottom: 0;
    background: ${({ fullpage, isDarkTheme }) => fullpage ? 'transparent' : isDarkTheme ? '#2f2f2f' : 'white'};
    // border-top: 1px #efefef solid;
    box-shadow: ${({ fullpage }) => fullpage ? 'none' : '0 -5px 6px -6px rgba(0,0,0,.15)'};
    margin: 0;
    padding: 8px 0;
  }

  .ant-table-footer {
    padding: ${({ footer }) => !!footer ? '8px' : '0'} !important;
  }

  .ant-tabs-tab {
    border-color: ${({ fullpage }) => fullpage ? '#dadada' : '#e4e4e4'} !important;
    background-color: ${({ fullpage }) => fullpage ? 'rgba(0,0,0,.04)' : 'rgba(0,0,0,.02)'} !important;
    color: #6a6a6a;
  }

  .ant-tabs-tab-active {
    border-bottom-color: ${({ fullpage }) => fullpage ? '#f0f2f5' : 'white'} !important;
    background-color: ${({ fullpage }) => fullpage ? 'transparent' : 'white'} !important;
  }

  .ant-tabs-nav::before {
    border-color: ${({ fullpage }) => fullpage ? '#dadada' : '#e4e4e4'} !important;
  }

  .ant-page-header-heading-title {
    ${({ embedded }) => embedded ? 'font-size: 16px' : ''};
  }
  .ant-page-header-heading-sub-title {
    ${({ embedded }) => embedded ? 'font-size: 12px' : ''};
  }
  .ant-tag {
    ${({ embedded }) => embedded ? 'font-size: 10px; line-height: 1.4; padding-left: 3px; padding-right: 3px;' : ''};
  }

  .ant-table-body {
    overflow-y: auto !important;
  }
`;

/*
const MIN_WIDTH_COL = 75;
const MIN_WIDTH_COL_FULL = 100;
const MAX_WIDTH_COL = 350;
const MAX_WIDTH_COL_FULL = 500;
*/

const getColumns = <RecordType extends Identifiable,>(
  fullpage = false,
  recordFields = {},
  deleteRecord: any,
  setRecordToEdit: any,
  navigate: any,
  recordModel?: RecordModel<RecordType>,
  recordsetFilters?: any,
  doRecordAction?: any,
  viewOnly?: boolean,
  fieldWhiteList?: string[],
  actionsWhitelist?: string[],
  setRecordForChangeLog?: (record: RecordType) => void,
  contentType?: IContentType,
) => {
  const customMappings: any = recordModel?.customTableMappings;
  const allColsSettings = { ellipsis: true };

  const defaultCols: any[] = Object.entries(recordFields)
    .filter(([k, v]: any) => !fieldWhiteList || fieldWhiteList.includes(k))
    .filter(([k, v]: any) => (!v.read_only || v.read_only || k === 'id' || (!!customMappings && !!customMappings[k])))
    .filter(([k, v]: any) => k !== 'cover_image')
    .filter(([k, v]: any) => !v.write_only)
    .filter(([k, v]) => customMappings ? !customMappings[k]?.omit : true)
    .filter(([k, v]: any) => !!recordsetFilters ? !Object.keys(recordsetFilters).includes(k) : true)
    .filter(([k, v]: any) => k !== 'object_id')
    .sort(([k, v]: any, [k2, v2]: any) => {
      let a: any = 100;
      let b: any = 100;
      if (!!customMappings) {
        a = customMappings[k]?.sort_index !== undefined ? customMappings[k]?.sort_index : 100;
        b = customMappings[k2]?.sort_index !== undefined ? customMappings[k2]?.sort_index : 100;
      }
      return a - b;
    })
    .map(([fieldName, fieldSchema]: any) => {
      const title = customMappings && customMappings[fieldName]?.title ? customMappings[fieldName]?.title : fieldSchema.label;
      let width = undefined;
      const align = !!customMappings ? customMappings[fieldName]?.align : undefined;
      if (fieldName === 'id') {
        width = 50
      } else {
        if (customMappings && customMappings[fieldName]?.width) {
          width = customMappings[fieldName]?.width;
        }
      }
      const customRender = customMappings ? customMappings[fieldName]?.render : null;
      const customPath = customMappings ? customMappings[fieldName]?.path : undefined;
      const dataIndex = customPath || fieldName;

      if ((fieldName.includes('amount') || fieldName.includes('price') || fieldName.includes('balance')) && !customRender) {
        return  { title, dataIndex, render: (r: any) => <>{financeAmtFormat(r)}</>, width, align: align || 'right' }
      }
      if (fieldSchema.type === 'file upload' || fieldSchema.type === 'image upload') {
        return  {
          title, dataIndex,
          render: (fileUrl: any) => fileUrl && /\.(jpeg|jpg|gif|png|bmp|webp|svg)$/i.test(fileUrl) ? <Image className="col-img" style={{ background: 'white' }} src={fileUrl} width={25} /> : fileUrl,
          width
        }
      }
      if (fieldSchema.type === 'boolean') {
        const titleWidth = title.length * 10;
        return {
          title,
          dataIndex,
          render: (r: boolean) => r ? <CheckCircleOutlined style={{ color: '#26b326', fontWeight: 600, fontSize: 16 }} /> : <CloseCircleOutlined style={{ color: '#f85959', fontSize: 16 }} />,
          width: titleWidth > 60 ? titleWidth : 60,
          align
        }
      }
      if (fieldName.includes('content_type')) {
        return {
          title: (r: any) => !!r.object_id ? "Related object" : title,
          render: (r: any) => {
            if (!!r.object_id) {
              return <GenericRelationLabel content_type={r.content_type} object_id={r.object_id} />
            } else {
              return typeof r[fieldName] === 'number' ? r[fieldName] : (!!r[fieldName] ? `${r[fieldName].app_label} | ${r[fieldName].model}` : '');
            }
          },
          width
        }
      }
      if (fieldName === 'id') {
        return {
          title: "ID",
          render: (r: any) => <p className="fs-1 pre-line" style={{ margin: 0 }}>{r.id}</p>,
          width
        }
      }
      if ((fieldName.includes('_at') || fieldName.includes('date') || fieldName.includes('period')) && fieldName !== 'periodicity') {
        return { title, dataIndex, render: (r: any) => moment(r).format('ll'), width: 115, align }
      }
      if (fieldName === 'timestamp') {
        return { title, dataIndex, render: (r: any) => moment(r).format('lll'), width: 180, align }
      }
      if (recordModel?.detailLinkField === fieldName) {
        return { title, render: (r: any) => <a href={`${window.location.href}/${r.id}/`}>{r[fieldName]}</a>, width }
      }
      if (customRender) {
        return { title, render: customRender, width, align };
      }
      if (fieldSchema.type === 'field' && !fieldSchema.child && recordModel && !fieldSchema.read_only) {
        return {
          title,
          render: (r: RecordType) => (
            <RelatedFieldCell<RecordType>
              fieldName={fieldName}
              fieldDefinition={fieldSchema}
              record={r}
              recordModel={recordModel}
            />
          ),
          width,
          align
        }
      }
      return { title, dataIndex, width, align };
    });

  if (Object.hasOwn(recordFields, 'cover_image') && (!fieldWhiteList || fieldWhiteList.includes('cover_image'))) {
    defaultCols.splice(1, 0, {
      title: 'Cover', dataIndex: 'cover_image',
      render: (imgUrl: string) => (
        <div style={{ margin: -8, height: 56 }}>
          <Image
            width={56}
            height={56}
            style={{ zIndex: 1 }}
            src={imgUrl}
          />
        </div>
      ),
      key: 'cover_image', width: 56 },);
  }

  (recordModel?.extraColumns || []).forEach(extraColumn => {
    defaultCols.push({
      key: extraColumn.key,
      title: extraColumn.title,
      width: extraColumn.width,
      align: extraColumn.align,
      render: (record: RecordType) => extraColumn.render(record, contentType),
    });
  });

  let actionsWidth: number | undefined = 0;
  if (!actionsWhitelist || actionsWhitelist.includes('edit')) actionsWidth += 62;
  if (!actionsWhitelist || actionsWhitelist.includes('delete')) actionsWidth += 70;
  if (recordModel?.hasImageGalleries && (!actionsWhitelist || actionsWhitelist.includes('gallery'))) actionsWidth += 80;
  if (recordModel?.floorPlanScope) actionsWidth += 95;
  if (!!recordModel?.customActions && recordModel.customActions.filter(ca => ca.isDetail && (ca.hidden === undefined ? true : !ca.hidden())).length > 0) actionsWidth += 90;
  if (!!recordModel?.detailAction) actionsWidth += 75;
  if (recordModel?.showsChangeLog) actionsWidth += 28;
  if (!!recordModel?.actionsChildren) actionsWidth = undefined;

  const actionsCol = {
    title: '',
    render: (r: any) => (
      <div className="row-actions">
        {(!actionsWhitelist || actionsWhitelist.includes('edit')) && (
          <a onClick={() => setRecordToEdit(r)}><EditOutlined /> Edit</a>
        )}

        {(recordModel?.hasImageGalleries && (!actionsWhitelist || actionsWhitelist.includes('gallery'))) && (
          <RecordGalleriesModal recordName={r.name} recordPath={recordModel.path} recordId={r[recordModel.primaryKeyName || 'id']}>
            <a><FolderAddOutlined /> Gallery</a>
          </RecordGalleriesModal>
        )}
        {recordModel?.floorPlanScope && (
          <FloorPlanWizard
            designId={recordModel.floorPlanScope === 'unit'
              ? (typeof r.design === 'object' ? r.design?.id : r.design)
              : r[recordModel.primaryKeyName || 'id']}
            unitId={recordModel.floorPlanScope === 'unit'
              ? r[recordModel.primaryKeyName || 'id']
              : undefined}
            recordName={r.name}
          >
            <a><PictureOutlined /> Floor plan</a>
          </FloorPlanWizard>
        )}
        {(!!recordModel?.customActions) && recordModel?.customActions.filter(ca => ca.isDetail && !ca.hidden?.(r)).map(ca =>
          <><RecordCustomAction<RecordType> customAction={ca} recordModel={recordModel} record={r} doRecordAction={doRecordAction} /></>
        )}
        {!!recordModel?.detailAction && (
          <a onClick={() => navigate(`${typeof recordModel.detailAction === 'string' ? recordModel.detailAction+'/' : ''}${r.id}`)}>
            <LoginOutlined /> Detail
          </a>
        )}

        {recordModel?.actionsChildren}

        {(!actionsWhitelist || actionsWhitelist.includes('delete')) && (
        <Popconfirm
          title={`Permanently delete this record?`}
          onConfirm={() => deleteRecord(r[recordModel?.primaryKeyName || 'id'])}
          okText="Yes"
        >
          <a><DeleteOutlined /> Delete</a>
        </Popconfirm>
        )}

        {recordModel?.showsChangeLog && (
          <Tooltip title="View change log">
            <a onClick={() => setRecordForChangeLog?.(r)}><HistoryOutlined /></a>
          </Tooltip>
        )}
      </div>
    ),
    width: actionsWidth,
  };

  return (!!viewOnly ? defaultCols : defaultCols.concat(actionsCol)).map((e: any) => ({ ...e, ...allColsSettings }));
}

const renderTabBar: TabsProps['renderTabBar'] = (props, DefaultTabBar) => {
  const DefaultTabBarComponent = DefaultTabBar as any;
  return <DefaultTabBarComponent {...props} className="sidento sticky-tab-bar" style={{ height: 32 }} />
};

interface RecordTableProps<RecordType> {
  recordModel: RecordModel<RecordType>;
  recordsetFilters?: RecordsetFilters;
  customRecordName?: string;
  className?: string;
  footer?: (records: RecordType[]) => ReactNode;
  noPagination?: boolean;
  fullpage?: boolean;
  modalView?: boolean;
  embedded?: boolean;
  btnSize?: "small" | "middle" | "large";
  btnBlock?: boolean;
  btnType?: ButtonType;
  btnText?: string;
  onBtnClick?: any;
  children?: ReactNode;
  highlightRecords?: number[] | string[];
  stickyTop?: number;
  viewOnly?: boolean;
  firstFetchQueryAppend?: object;
  size?: SizeType;
  extraQuery?: string;
  allowRowSelect?: boolean;
  headerExtra?: ReactNode;
  fieldWhitelist?: string[];
  actionsWhitelist?: string[];
  columnMapper?: (cols: ColumnsType<RecordType>) => ColumnsType<RecordType>;
}

const identity = <T,>(d: ColumnsType<T>) => d;

function ForRecordTable<RecordType extends Identifiable>(props: RecordTableProps<RecordType>, ref: any) {
  const { columnMapper = identity } = props;
  const navigate = useNavigate();
  const { query, handleFilter, handleSearch } = useQuery();
  const [currentTab, setCurrentTab] = useState(query.get('tab') || 'all');
  const { recordModel, recordsetFilters, extraQuery = '', allowRowSelect = false, modalView = false } = props;
  const { recordModel: { crossRecordViews = undefined } } = props;
  const filtersQuery = recordsetFilters ? '&' + Object.entries(recordsetFilters || {}).map(([k, v]) => `${k}=${v}`).join('&') : '';
  const highlightsQuery = props.highlightRecords ? `&highlights=${props.highlightRecords.join(',')}` : '';
  const { currentTheme } = useTheme();
  const isDarkTheme = currentTheme === 'dark';
  const {
    pagination,
    createRecord,
    deleteRecord,
    patchRecord,
    putRecord,
    handleChange,
    isLoading,
    recordList,
    recordFields,
    firstFetch,
    doRecordAction,
    contentType
  } = usePaginatedRecordList<any>({
    query,
    recordPath: recordModel.path,
    extraQueryAppend: recordModel.tableQuery + highlightsQuery + filtersQuery + extraQuery,
    apiService: 'admin',
    pageSize: recordModel.pageSize ? recordModel.pageSize.toString() : '25',
    initialOptionsFetch: true,
    reloadOnEdit: props.recordModel.reloadOnEdit
  });
  const elementRef: any = useRef(null);
  const [recordToEdit, setRecordToEdit] = useState<RecordType | null>(null);
  const [recordForChangeLog, setRecordForChangeLog] = useState<RecordType | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [nestedRecordFields, setNestedRecordFields] = useState<{ [field: string]: RecordFieldSchema }>();
  const getTableViewHeightOffset = () => {
    let defaultOffset = 135;
    if (!!props.recordModel.tableFilters || !!props.recordModel.allowSearch) {
      defaultOffset += 30;
    }
    if (crossRecordViews) {
      defaultOffset += 38;
    }
    if (props.size === 'middle') {
      defaultOffset += 12;
    }
    if (props.size === 'large') {
      defaultOffset += 27;
    }
    return defaultOffset;
  }
  const filterSize = crossRecordViews ? 'small' : 'middle';
  const recordsPluralName = props.customRecordName || (recordModel.namePlural ? recordModel.namePlural : `${recordModel.name}s`);

  useImperativeHandle(ref, () => ({
    reload: () => {
      firstFetch();
    }
  }));

  useEffect(() => {
    if (currentTab === 'all' && recordList.length === 0) {
      if (props.firstFetchQueryAppend) {  // I don't think this is ever used
        Object.entries(props.firstFetchQueryAppend).forEach(([k, v]) => {
          if (!query.get(k)) {
            handleFilter(k, v, true);
          }
        });
      }
      firstFetch();
    }
  }, [currentTab]);

  useEffect(() => {
    if (recordsetFilters) {
      firstFetch();
    }
  }, [recordsetFilters?.unit, recordsetFilters]);

  useEffect(() => {
    if (props.highlightRecords && props.highlightRecords.length > 0) {
      firstFetch();
    }
  }, [props.highlightRecords]);

  useEffect(() => {
    if (!!recordFields) {
      // Check if all fields are nested
      if (!Object.entries(recordFields).some(([_, v]: any) => v.type !== 'field' && v.type !== 'nested object')) {
        setNestedRecordFields(recordFields[recordModel.name.toLowerCase()]?.children);
      }
    }
  }, [recordModel, recordFields]);

  const MainRecordTable = (
    <>
      <div className="disp-flex h-stack-strict center gap-xs" style={{ marginBottom: props.embedded ? 8 : 16 }}>
      {props.recordModel.allowSearch && (
        <Search
          placeholder="Search..."
          onSearch={handleSearch}
          defaultValue={query.get('search') || ''}
          allowClear
          style={{ width: 300 }}
          size={filterSize}
        />
      )}
      {!(modalView || props.embedded) && props.recordModel.tableFilters && props.recordModel.tableFilters.map(tFilter => {
        if (tFilter.selectModel !== undefined) {
          return (
            <RecordSelect
              recordModel={tFilter.selectModel}
              filter={tFilter.filter || true}
              autoSelectFirst={tFilter.autoSelectFirst || false}
              extraFilterQuery={tFilter.extraFilterQuery}
              size={filterSize}
              {...tFilter.selectModelProps}
            />
          )
        }
        if (tFilter.component !== undefined) {
          return tFilter.component({ query, handleFilter, handleSearch }, { size: filterSize });
        }
        if (tFilter.selectField !== undefined && !!recordFields) {
          const selectFieldSchema: any = typeof tFilter.selectField === 'string' ? recordFields[tFilter.selectField] : path(tFilter.selectField, recordFields);
          if (!!selectFieldSchema && selectFieldSchema.type === 'choice') {
            return (
              <RecordFieldChoices defaultValue={tFilter.defaultValue} size={filterSize} fieldSchema={selectFieldSchema} filter />
            )
          }
        }
        return null;
      })}

      {recordsetFilters && (
        <div style={{ display: 'flex' }}>
          {Object.entries(recordsetFilters).map(([k, v]) => {
            return <Tag style={{ display: 'flex', alignItems: 'center', color: 'slategray', fontFamily: 'monospace' }}><>{k}: {v}</></Tag>
          })}
        </div>
      )}

      {selectedRowKeys.length > 0 && (
        <div className="disp-flex flx1 h-stack-strict center gap-xs" style={{ justifyContent: 'flex-end', marginBottom: -16 }}>
          <div>{selectedRowKeys.length} selected:</div>
          {/* <a onClick={() => {}}>Select all {pagination.total} records</a> */}
          {(!!recordModel?.customActions) && recordModel.customActions.filter(ca => ca.isDetail).map(ca => (
            <RecordCustomAction<RecordType> customAction={ca} recordModel={recordModel} record={{} as any} doRecordAction={doRecordAction} />
          ))}
          <Popconfirm
            title={`Permanently delete ${selectedRowKeys.length} records?`}
            onConfirm={() => {
              selectedRowKeys.forEach(key => {
                deleteRecord(key);
              });
              setSelectedRowKeys([]);
            }}
            okText="Yes"
            placement="bottomRight"
          >
            <Button type="link" icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </div>
      )}
      </div>

      <Table
        columns={columnMapper(getColumns<RecordType>(
          props.fullpage,
          nestedRecordFields || recordFields,
          deleteRecord,
          setRecordToEdit,
          navigate,
          recordModel,
          recordsetFilters,
          doRecordAction,
          props.viewOnly,
          props.fieldWhitelist,
          props.actionsWhitelist,
          setRecordForChangeLog,
          contentType,
        ))}
        dataSource={recordList}
        rowKey="id"
        pagination={props.noPagination ? false : pagination}
        loading={isLoading}
        onChange={handleChange}
        scroll={{ y: elementRef.current?.clientHeight - getTableViewHeightOffset() }}
        size={props.size || 'small'}
        footer={() => props.footer ? props.footer(recordList) : (modalView ? <div style={{ height: 4 }} /> : null)}
        sticky={{ offsetHeader: props.stickyTop || 0 }}
        summary={() => (<Table.Summary fixed="top"><Table.Summary.Row /></Table.Summary>)}
        rowSelection={allowRowSelect ? {
          selectedRowKeys,
          onChange: (selected: any) => {
            setSelectedRowKeys(selected);
          }
        } : props.highlightRecords ? {
          selectedRowKeys: props.highlightRecords,
          hideSelectAll: true,
          columnWidth: 0,
          renderCell: () => <></>
        } : undefined}
      />
    </>
  )

  const TableCont = (
    <Container
      ref={!!props.fullpage ? elementRef : null}
      className={`${props.className} ${props.fullpage ? 'anti-contento' : 'modal-anti-contento'}`}
      fullpage={props.fullpage}
      footer={props.footer}
      embedded={props.embedded}
      isDarkTheme={isDarkTheme}
    >
      <div className={`contento ${props.embedded ? 'pb-xs' : 'pb-sm'}`}>
        <PageHeader
          title={recordsPluralName}
          subTitle={pagination.total?.toLocaleString('en-US') + ' records'}
          extra={!!props.viewOnly ? [props.headerExtra] : [
            props.headerExtra,
            (
              (recordModel.customActions !== undefined) ?  recordModel.customActions.filter(ca => !ca.isDetail).map(ca => (
                <CustomActionButton<RecordType> customAction={ca} recordModel={recordModel} doRecordAction={doRecordAction} externalIsLoading={isLoading} />
              )) : undefined
            ),
            (
              (recordModel.adjacentModels !== undefined && !props.embedded) ? (
                recordModel.adjacentModels.map(mod => <RecordTable recordModel={mod} modalView />)
              ) : undefined
            ),
            (recordModel.allowCsvExport && (
              <ExportCsvModal recordModel={recordModel} recordFields={nestedRecordFields || recordFields} />
            )),
            (!props.viewOnly && (
              <RecordDrawer<RecordType>
                recordModel={recordModel}
                customCreateFields={recordModel.customCreateFields}
                saveAction={createRecord}
                recordsetFilters={props.recordsetFilters}
                customRecordName={props.customRecordName}
                disabled={isLoading}
                btnSize={props.embedded ? 'small' : undefined}
                btnType={props.embedded ? 'link' : undefined}
              />
            ))
          ]}
        />
      </div>

      {crossRecordViews !== undefined ? (
        <Tabs
          renderTabBar={renderTabBar}
          defaultActiveKey="all"
          activeKey={currentTab}
          className=""
          type="card"
          tabBarGutter={4}
          onTabClick={(key) => {
            // handleFilter('tab', key, true);
            const newUrl = window.location.href.split('?')[0] + '?tab='+key;
            window.history.pushState({ path: newUrl }, '', newUrl);
            setCurrentTab(key);
          }}
        >
          <Tabs.TabPane
            closable={false}
            tab={`All ${recordsPluralName.toLowerCase()}`}
            key="all"
            className="sidento"
          >
            {MainRecordTable}
          </Tabs.TabPane>
          {recordModel.crossRecordViews?.map(crv => (
            <Tabs.TabPane
              tab={crv.name || `${recordsPluralName} per ${crv.toModel?.name.toLowerCase()}`}
              key={crv.toModel?.path}
              className="sidento"
            >
              <ManyToManyRecordTable
                fromModel={recordModel}
                toModel={crv.toModel}
                toExtraQuery={crv.toExtraQuery}
                relName={crv.relName}
                invertedRel={crv.invertedRel}
                wrapperRef={elementRef}
                stickyTop={props.stickyTop}
              />
            </Tabs.TabPane>
          ))}
        </Tabs>
      ) : (
        <div className="sidento">
          {MainRecordTable}
        </div>
      )}
      <RecordDrawer<RecordType>
        recordModel={recordModel}
        customCreateFields={recordModel.customCreateFields}
        saveAction={recordModel.useMultipartFormData ? putRecord : patchRecord}
        recordsetFilters={props.recordsetFilters}
        customRecordName={props.customRecordName}
        recordInstance={recordToEdit}
        onClose={() => setRecordToEdit(null)}
      />
      {recordModel.showsChangeLog && recordForChangeLog && (
        <ChangeLogDrawer
          recordPath={recordModel.path}
          recordId={(recordForChangeLog as any)[recordModel.primaryKeyName || 'id']}
          recordName={recordModel.toString(recordForChangeLog)}
          fieldLabels={Object.fromEntries(
            Object.entries(recordFields || {}).map(([k, v]: any) => [k, v?.label || k])
          )}
          open={!!recordForChangeLog}
          onClose={() => setRecordForChangeLog(null)}
        />
      )}
      <br/>
    </Container>
  )

  if (modalView) {
    return (
      <>
        <Button
          block={props.btnBlock || false}
          size={props.btnSize}
          type={props.btnType}
          onClick={() => {
            setModalVisible(true);
            if (props.onBtnClick) props.onBtnClick();
          }}
        >
          {props.children ? (
            props.children
          ) : (
            <>
              {recordModel.icon || <SettingOutlined color="#fff0ff" />}

              {props.btnText ? (
                ` ${props.btnText}`
              ) : (
                <span> Manage {recordModel.namePlural ? recordModel.namePlural : `${recordModel.name}s`}</span>
              )}
            </>
            )
          }
        </Button>
        <Modal
          visible={modalVisible}
          onCancel={() => { setModalVisible(false); firstFetch(); }}
          footer={null}
          width={1100}
          title={(
            <div className="disp-flex h-stack-strict gap-xs center">
              {recordModel.icon || <SettingOutlined color="#fff0ff" />}
              <div>Manage {recordModel.name} Records</div>
            </div>
          )}
        >
          {TableCont}
        </Modal>
      </>
    )
  } else {
    return TableCont;
  }
}

const RecordTable = forwardRef(ForRecordTable) as <RecordType extends Identifiable>(
  props: RecordTableProps<RecordType> & { ref?: React.Ref<any> }
) => JSX.Element;
export default RecordTable;
